import { Card, Suit, Rank, SeatIndex } from "../types/game";

export const SUITS: Suit[] = ["C", "D", "H", "S"];
export const RANKS: Rank[] = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14];

// Game-point values per card (for the "Game" point).
export const GAME_POINTS: Record<Rank, number> = {
  2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0,
  10: 10, // Ten is worth 10 game points
  11: 1,  // Jack = 1
  12: 2,  // Queen = 2
  13: 3,  // King = 3
  14: 4,  // Ace = 4
};

export function newDeck(): Card[] {
  const deck: Card[] = [];
  for (const suit of SUITS) {
    for (const rank of RANKS) {
      deck.push({ suit, rank });
    }
  }
  return deck;
}

/** Cryptographically strong 32-bit seed for Mulberry32 deal shuffles. */
export function randomSeed(): number {
  const buf = new Uint32Array(1);
  globalThis.crypto.getRandomValues(buf);
  return buf[0]!;
}

// Mulberry32 — small, fast, seeded. Good enough for deal shuffles.
export function makeRng(seed: number): () => number {
  let t = seed >>> 0;
  return function () {
    t = (t + 0x6D2B79F5) >>> 0;
    let r = t;
    r = Math.imul(r ^ (r >>> 15), r | 1);
    r ^= r + Math.imul(r ^ (r >>> 7), r | 61);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

export function shuffle<T>(arr: T[], rng: () => number): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Deal 6 cards to each of 4 players, 3 at a time starting left of dealer.
// Left of dealer = (dealer + 1) % 4.
export function dealHands(
  deck: Card[],
  dealer: SeatIndex
): Card[][] {
  const hands: Card[][] = [[], [], [], []];
  let idx = 0;
  // Two rounds of 3 cards each, clockwise starting left of dealer
  for (let round = 0; round < 2; round++) {
    for (let i = 1; i <= 4; i++) {
      const seat = ((dealer + i) % 4) as SeatIndex;
      for (let c = 0; c < 3; c++) {
        hands[seat].push(deck[idx++]);
      }
    }
  }
  return hands;
}

export function nextSeat(seat: SeatIndex): SeatIndex {
  return ((seat + 1) % 4) as SeatIndex;
}

export function cardEquals(a: Card, b: Card): boolean {
  return a.suit === b.suit && a.rank === b.rank;
}

export function cardToStr(c: Card): string {
  const r = c.rank === 11 ? "J" : c.rank === 12 ? "Q" : c.rank === 13 ? "K" : c.rank === 14 ? "A" : String(c.rank);
  return `${r}${c.suit}`;
}
